import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaEye, FaTrash, FaCheck, FaTimes } from "react-icons/fa";
import {
  registrationService,
  WorkshopRegistration,
} from "../../services/firebase";
import "./Registrations.css";

const AdminRegistrations: React.FC = () => {
  const { t } = useTranslation();
  const [registrations, setRegistrations] = useState<WorkshopRegistration[]>(
    []
  );
  const [selectedRegistration, setSelectedRegistration] =
    useState<WorkshopRegistration | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewedRegistrations, setViewedRegistrations] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const fetchRegistrations = async () => {
    try {
      const data = await registrationService.getAll();
      setRegistrations(data);
    } catch (error) {
          }
  };

  const handleViewRegistration = (registration: WorkshopRegistration) => {
    setSelectedRegistration(registration);
    setIsModalOpen(true);
    // הוספת ההרשמה לרשימת ההרשמות הנצפות
    if (registration.id) {
      setViewedRegistrations((prev) => new Set(prev).add(registration.id!));
    }
  };

  const handleUpdateStatus = async (
    id: string,
    status: "pending" | "confirmed" | "cancelled"
  ) => {
    try {
      await registrationService.updateStatus(id, status);
      fetchRegistrations();
      if (selectedRegistration?.id === id) {
        setSelectedRegistration((prev) => (prev ? { ...prev, status } : null));
      }
    } catch (error) {
          }
  };

  const handleDeleteRegistration = async (id: string) => {
    if (window.confirm(t("admin.confirmDelete"))) {
      try {
        await registrationService.delete(id);
        fetchRegistrations();
        if (selectedRegistration?.id === id) {
          setIsModalOpen(false);
          setSelectedRegistration(null);
        }
      } catch (error) {
              }
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "תאריך לא ידוע";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString("he-IL", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "pending":
        return "ממתין לאישור";
      case "confirmed":
        return "מאושר";
      case "cancelled":
        return "בוטל";
      default:
        return status;
    }
  };

  return (
    <div className="registrations-page-container">
      <div className="registrations-header">
        <h1 className="registrations-title">{t("admin.registrations")}</h1>
      </div>

      <div className="registrations-grid">
        {registrations.length === 0 ? (
          <div className="empty-state">אין הרשמות לסדנאות</div>
        ) : (
          registrations.map((registration) => (
            <div
              key={registration.id}
              className={`registration-card ${
                registration.status || "pending"
              }`}
            >
              <div className="registration-header">
                <div className="registration-info">
                  <h3 className="workshop-title">
                    {registration.workshopTitle}
                  </h3>
                  <div className="participant-name">{registration.name}</div>
                  <div className="participant-contact">
                    {registration.email} • {registration.phone}
                    <div className="transaction-info">
                      <strong>מספר עסקה:</strong>{" "}
                      {registration.transactionId ||
                        registration.orderId ||
                        "לא זמין"}
                    </div>
                  </div>
                  <div className="registration-date">
                    {formatDate(registration.createdAt)}
                  </div>
                </div>
                <div className="registration-actions">
                  <span
                    className={`status-badge ${
                      registration.status || "pending"
                    }`}
                  >
                    {getStatusText(registration.status || "pending")}
                  </span>
                  <button
                    className={`action-button view ${
                      registration.id &&
                      viewedRegistrations.has(registration.id)
                        ? "viewed"
                        : ""
                    }`}
                    onClick={() => handleViewRegistration(registration)}
                    title="צפייה בפרטים"
                  >
                    <FaEye />
                  </button>
                  {registration.status === "pending" && (
                    <>
                      <button
                        className="action-button confirm"
                        onClick={() =>
                          registration.id &&
                          handleUpdateStatus(registration.id, "confirmed")
                        }
                        title="אשר הרשמה"
                      >
                        <FaCheck />
                      </button>
                      <button
                        className="action-button cancel"
                        onClick={() =>
                          registration.id &&
                          handleUpdateStatus(registration.id, "cancelled")
                        }
                        title="בטל הרשמה"
                      >
                        <FaTimes />
                      </button>
                    </>
                  )}
                  <button
                    className="action-button delete"
                    onClick={() =>
                      registration.id &&
                      handleDeleteRegistration(registration.id)
                    }
                    title="מחק הרשמה"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {isModalOpen && selectedRegistration && (
        <div className="registrations-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">
                הרשמה לסדנה: {selectedRegistration.workshopTitle}
              </h2>
              <div className="modal-meta">
                {formatDate(selectedRegistration.createdAt)} • סטטוס:{" "}
                {getStatusText(selectedRegistration.status || "pending")}
              </div>
            </div>
            <div className="modal-details">
              <h3>פרטי המשתתף:</h3>
              <p>
                <strong>שם:</strong> {selectedRegistration.name}
              </p>
              <p>
                <strong>אימייל:</strong> {selectedRegistration.email}
              </p>
              <p>
                <strong>טלפון:</strong> {selectedRegistration.phone}
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="registrations-button secondary"
                onClick={() => setIsModalOpen(false)}
              >
                סגירה
              </button>
              {selectedRegistration.status === "pending" && (
                <>
                  <button
                    className="registrations-button primary"
                    onClick={() =>
                      selectedRegistration.id &&
                      handleUpdateStatus(selectedRegistration.id, "confirmed")
                    }
                  >
                    אשר הרשמה
                  </button>
                  <button
                    className="registrations-button secondary"
                    onClick={() =>
                      selectedRegistration.id &&
                      handleUpdateStatus(selectedRegistration.id, "cancelled")
                    }
                  >
                    בטל הרשמה
                  </button>
                </>
              )}
              {selectedRegistration.id && (
                <button
                  className="registrations-button secondary"
                  onClick={() =>
                    handleDeleteRegistration(selectedRegistration.id!)
                  }
                >
                  {t("admin.delete")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRegistrations;
